import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API_BASE } from '../config';

export default function Signup() {
  const { signup, loading, error, clearError, user } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    name: '',
    email: '',
    mobile: '',
    password: '',
    confirmPassword: '',
    otp: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [formError, setFormError] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpMessage, setOtpMessage] = useState('');
  const [otpError, setOtpError] = useState('');

  useEffect(() => {
    if (user) navigate('/', { replace: true });
  }, [user, navigate]);

  useEffect(() => {
    clearError();
  }, []); // eslint-disable-line

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    setFormError('');
    if (name === 'mobile') {
      setOtpSent(false);
      setOtpMessage('');
      setOtpError('');
      setForm(prev => ({ ...prev, otp: '' }));
    }
  };

  const handleSendOtp = async () => {
    const mobile = form.mobile.trim();
    setOtpError('');
    setOtpMessage('');
    if (!mobile || !/^\+?[0-9]{10,15}$/.test(mobile)) {
      setOtpError('Enter a valid mobile number to receive OTP.');
      return;
    }

    setOtpLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/auth/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mobile }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to send OTP');
      setOtpSent(true);
      setOtpMessage(data.message || 'OTP sent successfully.');
    } catch (err) {
      setOtpError(err.message);
    } finally {
      setOtpLoading(false);
    }
  };

  const validate = () => {
    if (!form.name.trim()) return 'Full name is required.';
    if (form.name.trim().length < 2) return 'Name must be at least 2 characters.';
    if (!form.email.trim()) return 'Email is required.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return 'Enter a valid email address.';
    if (form.mobile.trim() && !/^\+?[0-9]{10,15}$/.test(form.mobile.trim())) return 'Enter a valid mobile number (10-15 digits).';
    if (form.mobile.trim() && !form.otp.trim()) return 'Please verify your mobile number by entering the OTP.';
    if (!form.password) return 'Password is required.';
    if (form.password.length < 8) return 'Password must be at least 8 characters.';
    if (form.password !== form.confirmPassword) return 'Passwords do not match.';
    if (!ageConfirmed) return 'You must confirm that you are 18 years or older.';
    return '';
  };

  const getPasswordStrength = (pw) => {
    if (!pw) return null;
    let score = 0;
    if (pw.length >= 8) score++;
    if (/[A-Z]/.test(pw)) score++;
    if (/[0-9]/.test(pw)) score++;
    if (/[^A-Za-z0-9]/.test(pw)) score++;
    if (score <= 1) return { label: 'Weak', color: '#e34234', width: '25%' };
    if (score === 2) return { label: 'Fair', color: '#ff9933', width: '50%' };
    if (score === 3) return { label: 'Good', color: '#d4a017', width: '75%' };
    return { label: 'Strong', color: '#4caf50', width: '100%' };
  };

  const strength = getPasswordStrength(form.password);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) { setFormError(err); return; }
    try {
      await signup(
        form.name.trim(),
        form.email.trim(),
        form.password,
        form.mobile.trim(),
        form.otp.trim(),
      );
      navigate('/', { replace: true });
    } catch {
      // error shown via context
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="auth-header">
          <span className="auth-icon">🌟</span>
          <h1>Create Account</h1>
          <p>Begin your celestial journey with AstroA2Z</p>
        </div>

        <form onSubmit={handleSubmit} noValidate>
          {(formError || error) && (
            <div className="auth-error">
              <span>⚠️</span> {formError || error}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="name">Full Name</label>
            <input
              id="name"
              name="name"
              type="text"
              autoComplete="name"
              placeholder="Your full name"
              value={form.name}
              onChange={handleChange}
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">Email Address</label>
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              placeholder="your@email.com"
              value={form.email}
              onChange={handleChange}
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="mobile">Mobile Number</label>
            <input
              id="mobile"
              name="mobile"
              type="tel"
              autoComplete="tel"
              placeholder="+919876543210"
              value={form.mobile}
              onChange={handleChange}
              disabled={loading}
            />
            <button
              type="button"
              className="btn btn-secondary auth-otp-send"
              onClick={handleSendOtp}
              disabled={loading || otpLoading || !form.mobile.trim()}
              style={{ marginTop: '0.75rem' }}
            >
              {otpLoading ? 'Sending OTP…' : otpSent ? 'Resend OTP' : 'Send OTP'}
            </button>
            {otpError && <p className="auth-note auth-note-error">{otpError}</p>}
            {otpMessage && <p className="auth-note auth-note-success">{otpMessage}</p>}
          </div>

          {otpSent && (
            <div className="form-group">
              <label htmlFor="otp">OTP Code</label>
              <input
                id="otp"
                name="otp"
                type="text"
                placeholder="Enter OTP"
                value={form.otp}
                onChange={handleChange}
                disabled={loading}
              />
            </div>
          )}

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <div className="auth-input-wrapper">
              <input
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                placeholder="Min. 8 characters"
                value={form.password}
                onChange={handleChange}
                disabled={loading}
              />
              <button
                type="button"
                className="auth-toggle-pw"
                onClick={() => setShowPassword(v => !v)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? '🙈' : '👁️'}
              </button>
            </div>
            {strength && (
              <div className="password-strength">
                <div
                  className="password-strength-bar"
                  style={{ width: strength.width, background: strength.color }}
                />
                <span style={{ color: strength.color }}>{strength.label}</span>
              </div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm Password</label>
            <div className="auth-input-wrapper">
              <input
                id="confirmPassword"
                name="confirmPassword"
                type={showConfirm ? 'text' : 'password'}
                autoComplete="new-password"
                placeholder="Repeat your password"
                value={form.confirmPassword}
                onChange={handleChange}
                disabled={loading}
              />
              <button
                type="button"
                className="auth-toggle-pw"
                onClick={() => setShowConfirm(v => !v)}
                aria-label={showConfirm ? 'Hide password' : 'Show password'}
              >
                {showConfirm ? '🙈' : '👁️'}
              </button>
            </div>
          </div>

          <p className="auth-consent-text" style={{ fontSize: '0.82rem', color: '#888', margin: '1rem 0 0.5rem', lineHeight: 1.5 }}>
            By creating an account, you agree to our{' '}
            <Link to="/terms-and-conditions">Terms &amp; Conditions</Link> and{' '}
            <Link to="/privacy-policy">Privacy Policy</Link>.
          </p>

          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.82rem', color: '#888', margin: '0.5rem 0 1rem', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={ageConfirmed}
              onChange={(e) => setAgeConfirmed(e.target.checked)}
              style={{ width: '16px', height: '16px', cursor: 'pointer' }}
            />
            I confirm that I am 18 years of age or older.
          </label>

          <button
            type="submit"
            className="btn btn-primary auth-submit"
            disabled={loading}
          >
            {loading ? <span className="auth-spinner" /> : 'Create Account 🚀'}
          </button>
        </form>

        <div className="auth-footer">
          <p>
            Already have an account?{' '}
            <Link to="/login" className="auth-link">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
